import type { Broadcast, BroadcastStartStopMethod } from '@/models/broadcast';
import type { Channel, LiveProgram } from '@/models/channel';
import type { Input } from '@/models/input';
import { FuelEntityType, FuelPortalService } from '@/services';
import {
  buildScheduleDisplayFromBroadcastSchedule,
  removeLiveProgramFromInputSchedule,
  syncInputScheduleForBroadcast,
} from '@/services/inputSchedule/inputSchedule.service';
import { LocalStorageService } from '@/services/local-storage';

/**
 * Data layer for Live Broadcast 2.0 (story 5936).
 *
 * Broadcast CRUD is still persisted to localStorage under `viewnexa.broadcasts2`
 * (no broadcasts-v2 backend exists yet — see feature 5924 deferred checklist).
 * Destination option lists are live: linear channels come from Fuel Portal
 * `Channel` entities.
 *
 * TODO(5936): replace the localStorage broadcast CRUD with a real broadcasts-v2 API.
 */

const STORAGE_KEY = LocalStorageService.LocalStorageItems.broadcasts2;
const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

const isGuid = (value?: string | null): value is string => !!value && GUID_REGEX.test(value);

const dedupeIds = (values: Array<string | null | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => !!value && value.trim().length > 0)));

export interface DestinationOption {
  value: string;
  label: string;
}

/** Form-local start/stop method derived from persisted hasSCTE + scheduleDisplay. */
export const deriveStartStopMethodFromLiveProgram = (program: LiveProgram | null | undefined): BroadcastStartStopMethod => {
  if (program?.hasSCTE) {
    return 'SCTE-35';
  }

  const scheduleDisplay = program?.metadata?.scheduleDisplay;
  const status = scheduleDisplay?.status;
  const startTime = scheduleDisplay?.startTime?.toString().trim() ?? '';
  const endTime = scheduleDisplay?.endTime?.toString().trim() ?? '';
  if (status === 'Scheduled' || startTime.length > 0 || endTime.length > 0) {
    return 'Schedule';
  }

  return 'Manual';
};

export type StartStopMethodDeriveInput = {
  hasSCTE?: boolean;
  scheduleDisplayStatus?: string | null;
  scheduleDisplayStartTime?: string | null;
  scheduleDisplayEndTime?: string | null;
};

export const deriveStartStopMethodFromFlags = (input: StartStopMethodDeriveInput): BroadcastStartStopMethod => {
  if (input.hasSCTE) {
    return 'SCTE-35';
  }

  const startTime = input.scheduleDisplayStartTime?.trim() ?? '';
  const endTime = input.scheduleDisplayEndTime?.trim() ?? '';
  if (input.scheduleDisplayStatus === 'Scheduled' || startTime.length > 0 || endTime.length > 0) {
    return 'Schedule';
  }

  return 'Manual';
};

const mergeOptionsByValue = (baseOptions: DestinationOption[], additionalOptions: DestinationOption[]): DestinationOption[] => {
  const merged = new Map<string, DestinationOption>();

  baseOptions.forEach((option) => {
    if (!option?.value) return;
    merged.set(option.value, option);
  });

  additionalOptions.forEach((option) => {
    if (!option?.value) return;
    const existing = merged.get(option.value);
    if (!existing || !existing.label) {
      merged.set(option.value, option);
    }
  });

  return Array.from(merged.values());
};

const readAll = (): Broadcast[] => {
  const stored = LocalStorageService.getItem(STORAGE_KEY);
  return Array.isArray(stored) ? (stored as Broadcast[]) : [];
};

const writeAll = (broadcasts: Broadcast[]): void => {
  LocalStorageService.setItem(STORAGE_KEY, broadcasts);
};

const generateId = (): string => `bx-${Date.now().toString(36)}`;

export type ManualLiveChangeEvent = {
  broadcastId: string;
  isLive: boolean;
};

type ManualLiveListener = (event: ManualLiveChangeEvent) => void;

const manualLiveListeners = new Set<ManualLiveListener>();

export const subscribeManualLive = (listener: ManualLiveListener): (() => void) => {
  manualLiveListeners.add(listener);
  return () => {
    manualLiveListeners.delete(listener);
  };
};

const notifyManualLiveChange = (broadcastId: string, isLive: boolean): void => {
  const event: ManualLiveChangeEvent = { broadcastId, isLive };
  manualLiveListeners.forEach((listener) => listener(event));
};

export const listBroadcasts2 = async (ouId: string): Promise<Broadcast[]> => readAll().filter((b) => b.ouId === ouId);

export const listBroadcastsByInputSource = async (ouId: string, inputSourceId: string): Promise<Broadcast[]> =>
  readAll().filter((b) => b.ouId === ouId && b.inputSource === inputSourceId);

export const getBroadcast2 = async (id: string): Promise<Broadcast | null> => readAll().find((b) => b.id === id) ?? null;

export const upsertBroadcast2 = async (broadcast: Broadcast): Promise<Broadcast> => {
  const all = readAll();
  const now = new Date().toISOString();
  const saved: Broadcast = { ...broadcast };

  if (!saved.id) {
    saved.id = generateId();
    saved.createdDate = now;
    saved.modifiedDate = now;
    all.push(saved);
  } else {
    saved.modifiedDate = now;
    const index = all.findIndex((b) => b.id === saved.id);
    if (index >= 0) {
      saved.createdDate = all[index].createdDate ?? now;
      all[index] = saved;
    } else {
      saved.createdDate = now;
      all.push(saved);
    }
  }

  writeAll(all);
  return saved;
};

export type SaveBroadcastFlowScaffoldContext = {
  isNew: boolean;
  previousInputSourceId?: string | null;
  nextInputSourceId?: string | null;
  timezone?: string;
  /** Form-local only; persisted as hasSCTE + scheduleDisplay + invertSCTE. */
  startStopMethod: BroadcastStartStopMethod;
  scteInvert: boolean;
};

export const saveBroadcastFlowScaffold = async (
  broadcast: Broadcast,
  context: SaveBroadcastFlowScaffoldContext
): Promise<Broadcast> => {
  const previousInputId = context.previousInputSourceId ?? '';
  const nextInputId = context.nextInputSourceId ?? '';
  const inputSourceChanged = previousInputId !== nextInputId;
  const startStopMethod = context.startStopMethod;
  const isScteMethod = startStopMethod === 'SCTE-35';

  const candidateId = isGuid(broadcast.id) ? broadcast.id : null;
  let existingLiveProgram: LiveProgram | null = null;
  if (candidateId) {
    existingLiveProgram = await FuelPortalService.getEntityById<LiveProgram>(FuelEntityType.LiveProgram, candidateId, {
      fullInfo: true,
    });
  }

  // Schedule data pulled in-memory from the selected input source; persisted (minus index)
  // onto the live program's scheduleDisplay. Preserve any existing index/metadata on update.
  const mappedScheduleDisplay = buildScheduleDisplayFromBroadcastSchedule(
    broadcast.schedule,
    context.timezone ?? '',
    startStopMethod
  );
  const existingScheduleDisplay = existingLiveProgram?.metadata?.scheduleDisplay;
  const scheduleDisplay = mappedScheduleDisplay
    ? {
        ...existingScheduleDisplay,
        ...mappedScheduleDisplay,
        ...(existingScheduleDisplay?.index != null ? { index: existingScheduleDisplay.index } : {}),
      }
    : existingScheduleDisplay;

  const liveProgramPayload: LiveProgram = {
    ...(existingLiveProgram ?? {}),
    ...(candidateId ? { id: candidateId } : {}),
    ouId: broadcast.ouId,
    entityType: FuelEntityType.LiveProgram,
    name: broadcast.title,
    description: broadcast.description ?? existingLiveProgram?.description ?? '',
    enabled: existingLiveProgram?.enabled ?? true,
    inputId: nextInputId || null,
    hasSCTE: isScteMethod,
    hasGeofencing: Boolean(broadcast.hasGeofencing),
    zipcodes: broadcast.hasGeofencing ? broadcast.zipcodes ?? '' : '',
    channelIds: dedupeIds(broadcast.destinations?.linearChannels ?? []),
    metadata: {
      ...existingLiveProgram?.metadata,
      invertSCTE: isScteMethod && Boolean(context.scteInvert),
      ...(scheduleDisplay ? { scheduleDisplay } : {}),
    } as LiveProgram['metadata'],
  };

  const savedLiveProgram = await FuelPortalService.upsertEntity<LiveProgram>(FuelEntityType.LiveProgram, liveProgramPayload);
  const liveProgramId = savedLiveProgram?.id ?? candidateId;

  if (isGuid(liveProgramId) && context.timezone) {
    const timezone = context.timezone;
    if (inputSourceChanged && previousInputId) {
      await removeLiveProgramFromInputSchedule(previousInputId, liveProgramId, timezone);
    }
    if (nextInputId) {
      await syncInputScheduleForBroadcast(nextInputId, broadcast, liveProgramId, timezone, startStopMethod);
    }
  }

  if (isGuid(liveProgramId)) {
    // Keep writes bounded to at most two inputs: previous + next.
    const targetInputIds = dedupeIds([previousInputId, nextInputId]);
    const inputUpdates = await Promise.all(
      targetInputIds.map(async (inputId) => {
        const currentInput = await FuelPortalService.getEntityById<Input>(FuelEntityType.Input, inputId, { fullInfo: true });
        if (!currentInput?.id) return null;

        const originalIds = dedupeIds(currentInput.liveProgramIds ?? []);
        let nextIds = originalIds;

        if (inputSourceChanged && inputId === previousInputId) {
          nextIds = originalIds.filter((id) => id !== liveProgramId);
        }
        if (inputId === nextInputId) {
          nextIds = dedupeIds([...nextIds, liveProgramId]);
        }

        if (nextIds.join('|') === originalIds.join('|')) {
          return null;
        }

        return {
          ...currentInput,
          liveProgramIds: nextIds,
        } as Input;
      })
    );

    const toPersist = inputUpdates.filter((item): item is Input => !!item);
    if (toPersist.length > 0) {
      await Promise.all(toPersist.map((input) => FuelPortalService.upsertEntity<Input>(FuelEntityType.Input, input)));
    }
  }

  const liveProgramThumbnailPreview =
    savedLiveProgram?.metadata?.thumbnail?.previewUri ?? existingLiveProgram?.metadata?.thumbnail?.previewUri ?? '';

  const broadcastToPersist: Broadcast = {
    ...broadcast,
    id: liveProgramId ?? broadcast.id,
    thumbnail: liveProgramThumbnailPreview || broadcast.thumbnail || '',
    thumbnailSrc: liveProgramThumbnailPreview || broadcast.thumbnail || null,
    ingestFile: null,
  };
  const saved = await upsertBroadcast2(broadcastToPersist);
  if (context.isNew) {
    void context.isNew;
  }
  return saved;
};

export const listLinearChannelOptions = async (ouId: string): Promise<DestinationOption[]> => {
  if (!ouId) return [];
  try {
    const result = await FuelPortalService.getList(FuelEntityType.Channel, {
      enabled: true,
      ouId,
      page: 0,
      size: 500,
      sortBy: 'name',
      orderBy: 'asc',
      showDeleted: false,
      useCompactChannel: true,
      excludeSubtype: ['MediaLibrary'],
      showLiveEnabled: true,
    });
    const channels: Channel[] = result?.data ?? [];
    return channels.map((channel) => ({ value: channel.id, label: channel.name }));
  } catch (ex) {
    console.error('Unable to load linear channel options', { ex });
    return [];
  }
};

export const hydrateSelectedLinearChannelOptions = async (
  selectedChannelIds: string[],
  existingOptions: DestinationOption[]
): Promise<DestinationOption[]> => {
  const uniqueSelectedIds = dedupeIds(selectedChannelIds);
  if (uniqueSelectedIds.length === 0) {
    return existingOptions;
  }

  const optionValues = new Set(existingOptions.map((option) => option.value));
  const missingIds = uniqueSelectedIds.filter((id) => !optionValues.has(id));
  if (missingIds.length === 0) {
    return existingOptions;
  }

  try {
    const channels = await FuelPortalService.batchGet<Channel[]>(FuelEntityType.Channel, missingIds);
    const hydratedOptions = (Array.isArray(channels) ? channels : [])
      .filter((channel): channel is Channel & { id: string } => Boolean(channel?.id))
      .map((channel) => ({
        value: channel.id,
        label: channel.name || channel.id,
      }));

    return mergeOptionsByValue(existingOptions, hydratedOptions);
  } catch (ex) {
    console.error('Unable to hydrate selected linear channel options', { ex });
    return existingOptions;
  }
};

export const addBroadcastDuration = async (broadcastId: string, minutes: number): Promise<Broadcast | null> => {
  const all = readAll();
  const index = all.findIndex((b) => b.id === broadcastId);
  if (index < 0) return null;

  const updated: Broadcast = {
    ...all[index],
    schedule: {
      ...all[index].schedule,
      duration: (all[index].schedule?.duration ?? 0) + minutes,
    },
    modifiedDate: new Date().toISOString(),
  };
  all[index] = updated;
  writeAll(all);
  return updated;
};

export const setBroadcastLiveState = async (broadcastId: string, isLive: boolean): Promise<Broadcast | null> => {
  const all = readAll();
  const index = all.findIndex((b) => b.id === broadcastId);
  if (index < 0) return null;

  const updated: Broadcast = {
    ...all[index],
    isLive,
    modifiedDate: new Date().toISOString(),
  };
  all[index] = updated;
  writeAll(all);
  notifyManualLiveChange(broadcastId, isLive);
  return updated;
};
